import { AfterViewInit, Component, EventEmitter, Input, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { select, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { CategoryNavigationService } from '~app/modules/generic/services/category-navigation.service';
import * as fromHeader from '../../../store';
import { Observable, Subject } from 'rxjs';
import { IAccountInformation } from '~shared/models';
import * as fromUserInfoSelectors from '~shared/modules/user-info/store/user-info.selectors';
import { ScrollService } from '~app/modules/generic/services/scroll.service';
import {
  GaTrackingEventAction,
  GaTrackingEventCategory,
  GATrackingEventType,
} from '~shared/modules/ga-tracking/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { RegionSelectorService } from '~shared/modules/region-selector/services';

@Component({
  selector: 'app-mobile-sidenav',
  templateUrl: './mobile-sidenav.component.html',
  styleUrls: ['./mobile-sidenav.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('visible', style({ backgroundColor: 'rgba(51, 67, 91, 0.9)', display: 'flex' })),
      state('hidden', style({ backgroundColor: 'transparent', display: 'none' })),
      transition('visible => hidden', animate('0.2s')),
      transition('hidden => visible', animate('0.2s')),
    ]),
  ],
})
export class MobileSidenavComponent implements AfterViewInit, OnInit {
  @Input() routeChangeEmission: EventEmitter<void>;

  private destroySubject$ = new Subject<boolean>();

  isOpened = false;
  user$: Observable<IAccountInformation> = this.store.pipe(
    select(fromUserInfoSelectors.getUserData),
    takeUntil(this.destroySubject$)
  );

  constructor(
    private categoryNavigationService: CategoryNavigationService,
    private scrollService: ScrollService,
    private store: Store<fromHeader.BuyerState>,
    private gtmDataLayerService: GtmDatalayerService,
    private readonly regionSelectorService: RegionSelectorService
  ) {}

  ngAfterViewInit(): void {
    this.subscribeToSidenavChanges();
    this.subscribeToRegionChanges();
  }

  ngOnInit(): void {
    this.subscribeToRouteChangeEmission();
  }

  ngOnDestroy(): void {
    this.closeSidenav();
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  private subscribeToRouteChangeEmission(): void {
    this.routeChangeEmission.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.closeSidenav();
    });
  }

  private fireSidenavEvent(state: string): void {
    this.gtmDataLayerService.gtagEvent({
      category: GaTrackingEventCategory.Navigation,
      action: GaTrackingEventAction.NavigationClick,
      event: GATrackingEventType.Navigation,
      label: `${state} Mobile Menu`,
    });
  }

  private openSidenav(): void {
    this.isOpened = true;
    this.fireSidenavEvent('Open');
    this.scrollService.deactivateScroll();
  }

  closeSidenav(): void {
    this.isOpened = false;
    this.fireSidenavEvent('Close');
    this.scrollService.activateScroll();
  }

  subscribeToSidenavChanges(): void {
    this.categoryNavigationService.sidenav$.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.openSidenav();
    });
  }

  subscribeToRegionChanges() {
    this.regionSelectorService.userRegion$.pipe(takeUntil(this.destroySubject$)).subscribe((region) => {
      this.closeSidenav();
    });
  }
}
